import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { interpretarGraficaSuperEjecutivo } from '../super-ejecutivo-grafica/super-ejecutivo-grafica.util';
import { LENGUAJE_BLOQUE_GRAFICA_SUPER_EJECUTIVO, MENSAJES_GRAFICA_SUPER_EJECUTIVO } from '../super-ejecutivo.constants';
import { ISegmentoRespuestaSuperEjecutivo } from '../super-ejecutivo.interface';

/**
 * Markdown del Super Ejecutivo a HTML seguro (RN11, RN11.1). El Super Ejecutivo escribe Markdown (GFM) con tablas; un salto de
 * línea simple es un salto de línea, como en sus propuestas. El HTML se limpia con DOMPurify: si el
 * modelo escribiera HTML o scripts, no se ejecutan.
 */
export function markdownSuperEjecutivoAHtml(texto: string): string {
  const html = marked.parse(texto, { gfm: true, breaks: true, async: false }) as string;

  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

const APERTURA_GRAFICA = `\`\`\`${LENGUAJE_BLOQUE_GRAFICA_SUPER_EJECUTIVO}`;
const ABRE_GRAFICA = new RegExp(`^${APERTURA_GRAFICA}\\s*$`);
const CIERRA_BLOQUE = /^```\s*$/;

/**
 * La última línea de un texto que sigue llegando es el principio de "```grafica" ("```g", "```gra"): más
 * larga que la cerca sola, que también cierra cualquier otro bloque de código.
 */
const esPrefijoDeApertura = (linea: string) => {
  const sinEspacios = linea.trimEnd();

  return sinEspacios.length > 3 && APERTURA_GRAFICA.startsWith(sinEspacios);
};

/**
 * Separa la respuesta en Markdown y bloques de gráfica (requerimiento `super-ejecutivo-graficas`, RN1). Un
 * bloque ```grafica cerrado es una gráfica válida o inválida (RN8.1). Uno sin cerrar al final del texto
 * todavía está llegando por el stream si el mensaje sigue `enCurso` (RN8.2); si el mensaje ya terminó
 * (historial, propuesta, el modelo nunca lo cerró), es una gráfica inválida (RN8.1); por eso `enCurso` es
 * false salvo que se diga lo contrario. Los demás bloques de código se quedan en el Markdown.
 */
export function segmentarRespuestaSuperEjecutivo(texto: string, enCurso = false): ISegmentoRespuestaSuperEjecutivo[] {
  const segmentos: ISegmentoRespuestaSuperEjecutivo[] = [];
  let markdown: string[] = [];
  let json: string[] | null = null;
  const lineas = texto.replace(/\r\n/g, '\n').split('\n');

  const cerrarMarkdown = () => {
    const contenido = markdown.join('\n');

    if (contenido.trim() !== '') {
      segmentos.push({ tipo: 'markdown', texto: contenido });
    }

    markdown = [];
  };

  // Mientras llega la línea de apertura por el stream, su prefijo no se pinta como Markdown (parpadearía
  // como un bloque de código vacío): ya cuenta como gráfica en camino.
  if (enCurso && lineas.length > 0 && esPrefijoDeApertura(lineas[lineas.length - 1])) {
    lineas[lineas.length - 1] = APERTURA_GRAFICA;
  }

  for (const linea of lineas) {
    if (json === null) {
      if (ABRE_GRAFICA.test(linea)) {
        cerrarMarkdown();
        json = [];
      } else {
        markdown.push(linea);
      }
    } else if (CIERRA_BLOQUE.test(linea)) {
      const resultado = interpretarGraficaSuperEjecutivo(json.join('\n'));

      segmentos.push('grafica' in resultado ? { tipo: 'grafica', grafica: resultado.grafica } : { tipo: 'graficaInvalida', motivo: resultado.motivo });
      json = null;
    } else {
      json.push(linea);
    }
  }

  if (json === null) {
    cerrarMarkdown();
  } else if (enCurso) {
    segmentos.push({ tipo: 'graficaIncompleta' });
  } else {
    segmentos.push({ tipo: 'graficaInvalida', motivo: MENSAJES_GRAFICA_SUPER_EJECUTIVO.GRAFICA_SIN_CERRAR });
  }

  return segmentos;
}
